import React from 'react';
import { Alert, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useHelpers from '../../hooks/context/useHelpers';
import usePaymentSetup from '../../hooks/onboarding/usePaymentSetup';
import usePaymentReturnPoll from '../../hooks/onboarding/usePaymentReturnPoll';
import usePermissions from '../../hooks/auth/usePermissions';
import useHistory from '../../hooks/navigation/useHistory';
import { ScreenEnum } from '../../hooks/navigation/Screens';
import { ManagerPermission } from '../../services/permissions/PermissionResolver';
import { CompanyOperationalStatus } from '../../services/exports/Constants';
import {
  PaymentSetupStatus,
  onlinePaymentStepPath,
} from '../../views/restaurant-onboarding/phases/payoutsPhaseHelpers';
import OnboardingLinkButton from '../buttons/OnboardingLinkButton';
import HostedOnboardingButton from '../buttons/HostedOnboardingButton';
import { isPlgOnboardingProvider } from '../../services/tenant/plgOnboarding';

/** The partner-restaurant states the owner has to act on, and how loudly. */
const PARTNER_BANNERS = {
  [PaymentSetupStatus.ActionNeeded]: 'error',
  [PaymentSetupStatus.LinkExpired]: 'warning',
  [PaymentSetupStatus.HostedIncomplete]: 'warning',
};

/**
 * Only once the restaurant is live: during onboarding the wizard is where these
 * are done, and it is on screen anyway. A restaurant that went live cash only
 * has no other reminder that these are still open.
 */
const LIVE_ONLY_BANNERS = {
  [PaymentSetupStatus.HostedNotStarted]: 'warning',
  [PaymentSetupStatus.BusinessDetailsMissing]: 'warning',
};

/**
 * A PLG partner (white-label) restaurant reads the same payment-setup status as the
 * wizard, so the banner and the Online payment step cannot disagree. Its CTA
 * mints a fresh provider link and goes straight there rather than through the
 * wizard: a restaurant that is already live — the usual case for "action
 * needed" — is sent home by the wizard, and could not reach its step at all.
 *
 * Master restaurants and legacy (non-PLG) partner restaurants keep the classic
 * per-gateway verification banner: they predate gateway connections, and the
 * status resolver would call every one of them "not set up".
 */
export default function PaymentGatewayKycBanner() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Layout:PaymentGatewayKycBanner' });
  const { company } = useSelector((state) => state.currentCompany);
  const { formatDateTime } = useHelpers();
  // The restaurant's own provider, not the one the viewer is acting as: a super
  // admin on a partner restaurant sees what the partner's owner sees.
  const isPartnerRestaurant = isPlgOnboardingProvider(company?.service_provider);
  // Reading the setup needs ManageBusinessProfiles and acting on it needs
  // WriteBusinessProfiles: without the first nothing is asked (no 403 on every
  // page), without the second the banner informs but offers no button.
  const { can } = usePermissions();
  const history = useHistory();
  const canRead = can(ManagerPermission.ManageBusinessProfiles);
  const canAct = can(ManagerPermission.WriteBusinessProfiles);
  const { setup, reload } = usePaymentSetup(isPartnerRestaurant && canRead ? company.id : null);
  // Just back from the provider: the owner has done what the banner would ask, and
  // the status says so a few seconds later. Asking again in between reads as failure.
  const checkingReturn = usePaymentReturnPoll(isPartnerRestaurant && canRead ? company.id : null, reload);

  function renderPartnerAction() {
    if (!canAct) {
      return null;
    }

    // Business details are ours, not the provider's: they are filled in on the
    // wizard's Online payment step, which opens for a live restaurant when the link names it.
    if (setup.status === PaymentSetupStatus.BusinessDetailsMissing) {
      return (
        <Button
          type="link"
          onClick={() => history.push(onlinePaymentStepPath(ScreenEnum.RestaurantOnboarding, company.id))}
        >
          {t('partner.continue')}
        </Button>
      );
    }

    return (
      <HostedOnboardingButton type="link" gateway={setup.hosted_gateway} errorMessage={t('partner.open_error')}>
        {t('partner.continue')}
      </HostedOnboardingButton>
    );
  }

  function renderPartnerBanner() {
    if (!setup || checkingReturn) {
      return null;
    }

    const isLive = company.operational_status === CompanyOperationalStatus.Live;
    const type = PARTNER_BANNERS[setup.status] ?? (isLive ? LIVE_ONLY_BANNERS[setup.status] : undefined);

    if (type) {
      return (
        <Alert
          banner
          type={type}
          data-testid="payment-setup-banner"
          title={
            <div className="flex items-center justify-between">
              <span>{t(`partner.${setup.status}`)}</span>
              {renderPartnerAction()}
            </div>
          }
        />
      );
    }

    // Submitted but the provider has set a date by which it wants more: the same
    // warning master restaurants get.
    const deadline = setup.hosted_gateway ? company.merchant?.[`${setup.hosted_gateway}_kyc_deadline`] : null;

    if (!deadline || setup.status === PaymentSetupStatus.Verified) {
      return null;
    }

    return (
      <Alert
        banner
        type="warning"
        data-testid="payment-setup-deadline-banner"
        title={
          <div className="flex items-center justify-between">
            <span>{t('deadline_warning', { date: formatDateTime(deadline) })}</span>
            {canAct && (
              <HostedOnboardingButton type="link" gateway={setup.hosted_gateway} errorMessage={t('partner.open_error')}>
                {t('complete_verification')}
              </HostedOnboardingButton>
            )}
          </div>
        }
      />
    );
  }

  if (!company || company.operational_status === CompanyOperationalStatus.Suspended) {
    return null;
  }

  if (isPartnerRestaurant) {
    return renderPartnerBanner();
  }

  if (!company.payment_gateway) {
    return null;
  }

  const gateway = company.payment_gateway;
  const deadline = company.merchant[`${gateway}_kyc_deadline`];

  if (!!company.payment_gateway_verification_status && !company.verified_payment_gateway) {
    return (
      <Alert
        banner
        type="error"
        title={
          <div className="flex items-center justify-between">
            <span>{t('paused')}</span>
            <OnboardingLinkButton paymentGateway={gateway} type="link" text={t('complete_verification')} />
          </div>
        }
      />
    );
  }

  if (deadline) {
    return (
      <Alert
        banner
        type="warning"
        title={
          <div className="flex items-center justify-between">
            <span>{t('deadline_warning', { date: formatDateTime(deadline) })}</span>
            <OnboardingLinkButton paymentGateway={gateway} type="link" text={t('complete_verification')} />
          </div>
        }
      />
    );
  }

  return null;
}
